import React, { useMemo, useRef, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../../app/store';
import { getUserProjects } from '../../../../app/features/sessions/projectsManagement/sessionManageProjects';
import { useNavigate, useParams } from 'react-router-dom';
import ProjectDetailsModal from '../../../../app/components/modal/projectModal/ProjectDetails';
import CreateProjectForm from '../../../../app/components/forms/projects/CreateProjectForm';
import { ProjectPlayer } from '../../../../app/components/audioPlayer/WaveSurfer/projectPlayer/ProjectPlayer';
import {
    Box, Chip, CircularProgress, Container, FormControl,
    IconButton, MenuItem, Select, SelectChangeEvent,
    Skeleton, Stack, Typography,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import { handlePrivateRoutes } from '../../../../app/api/requestRequirements';

const BG_MAIN        = '#15151a';
const BG_CARD        = '#24242e';
const BG_INPUT       = '#1c1c24';
const BRAND_RED      = '#c0392b';
const TEXT_PRIMARY   = '#f4f4f6';
const TEXT_SECONDARY = 'rgba(244,244,246,0.65)';
const BORDER_SUBTLE  = '1px solid rgba(255,255,255,0.08)';

const selectSx = {
    color: TEXT_PRIMARY,
    bgcolor: BG_INPUT,
    '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.15)' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: BRAND_RED },
    '.MuiSvgIcon-root': { color: TEXT_SECONDARY },
};

export default function ManageProjects() {
    const currentUser         = useSelector((state: RootState) => state.session.currentUser);
    const authHeaders         = useSelector((state: RootState) => state.session.authHeaders);
    const dispatch            = useDispatch<AppDispatch>();
    const loading             = useSelector((state: RootState) => state.sessionManageProjects.loading);
    const newPublicProjects   = useSelector((state: RootState) => state.sessionManageProjects.newPublicProjects);
    const newPrivateProjects  = useSelector((state: RootState) => state.sessionManageProjects.newPrivateProjects);
    const allUserRouteProjects = useSelector((state: RootState) => state.sessionManageProjects.allProjects);
    const loadingMoreProjects = useSelector((state: RootState) => state.sessionManageProjects.loadingMoreProjects);
    const loadingCreation     = useSelector((state: RootState) => state.sessionManageProjects.loadingCreation);

    const [showProjectDetails, setShowProjectDetails]   = useState(false);
    const [projectSelected, setProjectSelected]         = useState<any>();
    const [userRouteProjectsSelected, seUserRouteProjectsSelected] = useState(true);
    const [alienProjectsSelected, setAlienProjectsSelected]       = useState(false);
    const [allProjectsSelected, setAllProjectsSelected]           = useState(false);
    const [publicSelected, setPublicSelected]   = useState(true);
    const [privateSelected, setPrivateSelected] = useState(true);
    const [ownerFilter, setOwnerFilter]     = useState('userRouteProjectsOption');
    const [privacyFilter, setPrivacyFilter] = useState('publicPrivateOption');
    const [projectsPage, setProjectsPage]   = useState<number>(1);

    const { userNicknameRoute } = useParams<string>();
    const containerRef          = useRef<HTMLDivElement | null>(null);
    const navigate              = useNavigate();

    const MemorizedProjectPlayer = useMemo(() => React.memo(ProjectPlayer), []);

    useEffect(() => {
        if (currentUser && currentUser?.nickname !== userNicknameRoute) {
            const PRIVATE_ROUTES = handlePrivateRoutes({ ROUTE_PARAMS: { userNickname: currentUser.nickname } });
            navigate(PRIVATE_ROUTES.USER);
        }
        fetchProjects();
    }, [projectsPage, userNicknameRoute]);

    const fetchProjects = async () => {
        if (authHeaders && userNicknameRoute) {
            await dispatch(getUserProjects({
                authHeaders,
                urlParams: { userNickname: userNicknameRoute, page: projectsPage },
            }));
        }
    };

    function toggleDescriptionModal(item: any) {
        setShowProjectDetails(!showProjectDetails);
        setProjectSelected(item);
    }

    const handleOwnerFilter = (event: SelectChangeEvent) => {
        const value = event.target.value;
        setOwnerFilter(value);
        seUserRouteProjectsSelected(value === 'userRouteProjectsOption');
        setAlienProjectsSelected(value === 'alienProjectsOption');
        setAllProjectsSelected(value === 'allProjectsOption');
    };

    const handlePrivacyFilter = (event: SelectChangeEvent) => {
        const value = event.target.value;
        setPrivacyFilter(value);
        setPublicSelected(value === 'publicOption' || value === 'publicPrivateOption');
        setPrivateSelected(value === 'privateOption' || value === 'publicPrivateOption');
    };

    useEffect(() => {
        const handleScroll = () => {
            const container = containerRef.current;
            if (!container) return;
            const { scrollTop, clientHeight, scrollHeight } = container;
            if (scrollTop + clientHeight >= scrollHeight && !loadingMoreProjects) {
                setProjectsPage((prev) => prev + 1);
            }
        };
        const container = containerRef.current;
        if (container) {
            container.addEventListener('scroll', handleScroll);
            return () => container.removeEventListener('scroll', handleScroll);
        }
    }, [loadingMoreProjects]);

    const getOwnerNickname = (p: any) =>
        p.project.user?.userMoreInfo?.nickname ?? p.project.user?.nickname;

    const filteredProjects = useMemo(() => {
        if (publicSelected && !privateSelected && userRouteProjectsSelected) {
            return allUserRouteProjects.filter(p =>
                getOwnerNickname(p) === userNicknameRoute && p.project.privacity === 0);
        } else if (!publicSelected && privateSelected && userRouteProjectsSelected) {
            return allUserRouteProjects.filter(p =>
                getOwnerNickname(p) === userNicknameRoute && p.project.privacity === 1);
        } else if (publicSelected && !privateSelected && alienProjectsSelected) {
            return allUserRouteProjects.filter(p =>
                getOwnerNickname(p) !== userNicknameRoute && p.project.privacity === 0);
        } else if (!publicSelected && privateSelected && alienProjectsSelected) {
            return allUserRouteProjects.filter(p =>
                getOwnerNickname(p) !== userNicknameRoute && p.project.privacity === 1);
        } else if (publicSelected && !privateSelected && allProjectsSelected) {
            return allUserRouteProjects.filter(p => p.project.privacity === 0);
        } else if (!publicSelected && privateSelected && allProjectsSelected) {
            return allUserRouteProjects.filter(p => p.project.privacity === 1);
        } else if (publicSelected && privateSelected && userRouteProjectsSelected) {
            return allUserRouteProjects.filter(p =>
                getOwnerNickname(p) === userNicknameRoute);
        } else if (publicSelected && privateSelected && alienProjectsSelected) {
            return allUserRouteProjects.filter(p =>
                getOwnerNickname(p) !== userNicknameRoute);
        }
        return allUserRouteProjects;
    }, [alienProjectsSelected, userRouteProjectsSelected, privateSelected, publicSelected, userNicknameRoute, allUserRouteProjects]);

    const renderProjectItem = (item: any, index: number) => (
        <Box
            key={index}
            sx={{ borderBottom: BORDER_SUBTLE, px: 2, pt: 1.5, pb: 1, '&:last-child': { borderBottom: 'none' } }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography variant='body1' fontWeight={600} color={TEXT_PRIMARY} noWrap sx={{ flex: 1 }}>
                    {item.project.title}
                </Typography>
                <Chip
                    label={item.project.privacity === 0 ? 'PUBLIC' : 'PRIVATE'}
                    size='small'
                    sx={{
                        bgcolor: item.project.privacity === 0 ? 'rgba(35,148,0,0.18)' : 'rgba(192,57,43,0.18)',
                        color: item.project.privacity === 0 ? '#4caf50' : '#e57373',
                        fontWeight: 600, fontSize: '0.65rem', flexShrink: 0,
                    }}
                />
                <IconButton
                    size='small'
                    onClick={() => toggleDescriptionModal(item)}
                    sx={{ color: TEXT_SECONDARY, flexShrink: 0 }}
                >
                    <MoreVertIcon fontSize='small' />
                </IconButton>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, mb: item.masterFiles?.length > 0 ? 1 : 0 }}>
                <Typography variant='caption' color={TEXT_SECONDARY}>
                    Useful: <b style={{ color: TEXT_PRIMARY }}>{item.project.nUsefulContents}</b>
                </Typography>
                <Typography variant='caption' color={TEXT_SECONDARY}>
                    Other: <b style={{ color: TEXT_PRIMARY }}>{item.project.nUselessContents}</b>
                </Typography>
            </Box>
            {item.masterFiles && item.masterFiles.length > 0 && (
                <MemorizedProjectPlayer fileUrl={`${item.masterFiles[0].signedUrl}`} />
            )}
        </Box>
    );

    const isEmpty = filteredProjects.length === 0 && newPublicProjects.length === 0 && newPrivateProjects.length === 0;

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: BG_MAIN, py: 4 }}>
            <Container maxWidth='lg'>
                {/* Title */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant='h5' fontWeight={700} color={TEXT_PRIMARY}>
                        {userNicknameRoute === currentUser?.nickname ? 'My Projects' : 'User Projects'}
                    </Typography>
                    <Box sx={{ width: 40, height: 3, bgcolor: BRAND_RED, mt: 0.5 }} />
                </Box>

                {/* Create Project Form */}
                {currentUser?.nickname === userNicknameRoute && <CreateProjectForm />}

                {/* Filters */}
                <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
                    <FormControl size='small'>
                        <Select
                            value={ownerFilter}
                            onChange={handleOwnerFilter}
                            sx={selectSx}
                            MenuProps={{ PaperProps: { sx: { bgcolor: BG_INPUT, color: TEXT_PRIMARY } } }}
                        >
                            <MenuItem value='userRouteProjectsOption'>My Projects</MenuItem>
                            <MenuItem value='alienProjectsOption'>Alien Projects</MenuItem>
                            <MenuItem value='allProjectsOption'>All Projects</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl size='small'>
                        <Select
                            value={privacyFilter}
                            onChange={handlePrivacyFilter}
                            sx={selectSx}
                            MenuProps={{ PaperProps: { sx: { bgcolor: BG_INPUT, color: TEXT_PRIMARY } } }}
                        >
                            <MenuItem value='publicOption'>Public</MenuItem>
                            <MenuItem value='privateOption'>Private</MenuItem>
                            <MenuItem value='publicPrivateOption'>Public & Private</MenuItem>
                        </Select>
                    </FormControl>
                </Box>

                {/* Projects list */}
                <Box
                    ref={containerRef}
                    sx={{
                        bgcolor: BG_CARD,
                        borderRadius: '12px',
                        border: BORDER_SUBTLE,
                        overflow: 'hidden',
                        maxHeight: '65vh',
                        overflowY: 'auto',
                    }}
                >
                    {loadingCreation && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                            <CircularProgress size={24} sx={{ color: BRAND_RED }} />
                        </Box>
                    )}

                    {loading && !loadingCreation && Array.from({ length: 4 }).map((_, i) => (
                        <Box key={i} sx={{ px: 2, pt: 1.5, pb: 1, borderBottom: BORDER_SUBTLE }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                                <Skeleton variant='rounded' height={22} sx={{ bgcolor: 'rgba(255,255,255,0.08)', flex: 1 }} />
                                <Skeleton variant='rounded' width={58} height={20} sx={{ bgcolor: 'rgba(255,255,255,0.08)', flexShrink: 0 }} />
                                <Skeleton variant='circular' width={28} height={28} sx={{ bgcolor: 'rgba(255,255,255,0.08)', flexShrink: 0 }} />
                            </Box>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <Skeleton variant='rounded' width={72} height={16} sx={{ bgcolor: 'rgba(255,255,255,0.06)' }} />
                                <Skeleton variant='rounded' width={62} height={16} sx={{ bgcolor: 'rgba(255,255,255,0.06)' }} />
                            </Box>
                        </Box>
                    ))}

                    {publicSelected && userRouteProjectsSelected && newPublicProjects &&
                        newPublicProjects.map((item, index) => renderProjectItem(item, index))}

                    {privateSelected && userRouteProjectsSelected && newPrivateProjects &&
                        newPrivateProjects.map((item, index) => renderProjectItem(item, index))}

                    {filteredProjects && filteredProjects.map((item, index) => renderProjectItem(item, index))}

                    {loadingMoreProjects && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                            <CircularProgress size={24} sx={{ color: BRAND_RED }} />
                        </Box>
                    )}

                    {!loading && !loadingMoreProjects && isEmpty && (
                        <Stack alignItems='center' justifyContent='center' py={8}>
                            <MusicNoteIcon sx={{ fontSize: 56, color: TEXT_SECONDARY, mb: 1 }} />
                            <Typography variant='body2' color={TEXT_SECONDARY}>
                                No projects yet
                            </Typography>
                        </Stack>
                    )}
                </Box>
            </Container>

            {showProjectDetails && (
                <ProjectDetailsModal
                    projectInfoAndUrls={projectSelected}
                    close={() => setShowProjectDetails(false)}
                />
            )}
        </Box>
    );
}
